import React from "react";
import { ChevronDown, ChevronUp, LucidePlus, Trash2 } from "lucide-react";
import TextInput from "../../../../components/UI/TextInput";
import { Button } from "../../../../components/UI/Button";
import type { PortfolioButtonSize } from "../../../../../domain/types/content";
import {
  BUTTON_URL_HINT,
  PORTFOLIO_BUTTON_MAX_COUNT,
  PORTFOLIO_BUTTON_SIZES,
  PORTFOLIO_BUTTON_SIZE_LABELS,
  isAllowedButtonUrl,
} from "../constants";

/**
 * 편집 중인 버튼 한 줄.
 *
 * 저장 전에는 서버 id 가 없다. 배열 인덱스를 key 로 쓰면 중간 삭제 시
 * 입력 상태가 옆줄로 밀리므로, 화면 안에서만 쓰는 임시 key 를 붙인다.
 */
export type PortfolioButtonDraft = {
  key: string;
  label: string;
  url: string;
  size: PortfolioButtonSize;
};

let draftKeySeq = 0;

export const createButtonDraft = (
  values: Partial<Omit<PortfolioButtonDraft, "key">> = {},
): PortfolioButtonDraft => ({
  key: `portfolio-button-${(draftKeySeq += 1)}`,
  label: values.label ?? "",
  url: values.url ?? "",
  size: values.size ?? "MEDIUM",
});

interface PortfolioButtonsEditorProps {
  buttons: PortfolioButtonDraft[];
  onChange: (buttons: PortfolioButtonDraft[]) => void;
}

/**
 * 포트폴리오 본문 아래에 붙는 버튼 편집기.
 *
 * 마크다운 본문 안에 버튼 문법을 넣지 않고 목록으로 따로 관리한다.
 * 커스텀 문법은 파서 확장이 필요하고 XSS 표면을 넓히는 반면, 목록이면
 * label 과 url 을 각각 검증할 수 있다. 배열 순서가 곧 노출 순서다.
 */
export const PortfolioButtonsEditor = ({
  buttons,
  onChange,
}: PortfolioButtonsEditorProps) => {
  const patchButton = (
    index: number,
    patch: Partial<Omit<PortfolioButtonDraft, "key">>,
  ) => {
    onChange(
      buttons.map((button, i) =>
        i === index ? { ...button, ...patch } : button,
      ),
    );
  };

  const moveButton = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= buttons.length) return;

    const next = [...buttons];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  const isFull = buttons.length >= PORTFOLIO_BUTTON_MAX_COUNT;

  return (
    <div className="text-start mb-[1.0625rem]">
      <div className="flex flex-row items-center justify-between mb-2">
        <label className="block text-sm font-medium">
          버튼
          <span className="ml-2 text-[12px] text-[#888] font-normal">
            {buttons.length} / {PORTFOLIO_BUTTON_MAX_COUNT}
          </span>
        </label>
        <Button
          variant="outline"
          disabled={isFull}
          onClick={() => {
            if (isFull) return;
            onChange([...buttons, createButtonDraft()]);
          }}
        >
          <LucidePlus className="w-4 h-4" />
          버튼 추가
        </Button>
      </div>

      {isFull ? (
        <p className="mb-2 text-[12px] text-[#888]">
          버튼은 최대 {PORTFOLIO_BUTTON_MAX_COUNT}개까지 넣을 수 있습니다.
        </p>
      ) : null}

      {buttons.length === 0 ? (
        <p className="text-[12px] text-[#888]">
          본문 아래에 표시할 버튼이 없습니다. 견적 문의 링크처럼 방문자를 보낼
          곳이 있으면 추가해주세요.
        </p>
      ) : null}

      <div className="flex flex-col gap-[0.75rem]">
        {buttons.map((button, index) => {
          // 빈 칸은 아직 입력 중일 수 있으니 경고하지 않는다
          const hasInvalidUrl =
            button.url.trim() !== "" && !isAllowedButtonUrl(button.url);

          return (
            <div
              key={button.key}
              className="p-[0.875rem] / border border-[#e0e0e0] / rounded-[8px] / bg-gray-50"
            >
              <div className="flex flex-row items-center justify-between mb-[0.5rem]">
                <span className="text-[12px] text-[#888]">{index + 1}번째</span>
                <div className="flex flex-row items-center gap-1">
                  <button
                    type="button"
                    className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30"
                    disabled={index === 0}
                    onClick={() => moveButton(index, "up")}
                    aria-label="위로"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30"
                    disabled={index === buttons.length - 1}
                    onClick={() => moveButton(index, "down")}
                    aria-label="아래로"
                  >
                    <ChevronDown size={16} />
                  </button>
                  <button
                    type="button"
                    className="p-0.5 hover:bg-gray-200 rounded ml-1"
                    onClick={() =>
                      onChange(buttons.filter((_, i) => i !== index))
                    }
                    aria-label="삭제"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="mb-[0.5rem]">
                <div className="text-[12px] text-[#888] mb-[0.25rem]">문구</div>
                <TextInput
                  value={button.label}
                  onChange={(e) => patchButton(index, { label: e.target.value })}
                  placeholder="견적 문의하기"
                  className="bg-white"
                />
              </div>

              <div className="mb-[0.5rem]">
                <div className="text-[12px] text-[#888] mb-[0.25rem]">
                  이동 주소
                </div>
                <TextInput
                  value={button.url}
                  onChange={(e) => patchButton(index, { url: e.target.value })}
                  placeholder="https://example.com/contact"
                  className="bg-white"
                />
                <p
                  className={`mt-1 text-[12px] ${hasInvalidUrl ? "text-[#F24A4A]" : "text-[#888]"}`}
                >
                  {BUTTON_URL_HINT}
                </p>
              </div>

              <div>
                <div className="text-[12px] text-[#888] mb-[0.25rem]">크기</div>
                <div className="flex flex-row gap-x-[0.5rem]">
                  {PORTFOLIO_BUTTON_SIZES.map((size) => (
                    <div
                      key={size}
                      role="button"
                      onClick={() => patchButton(index, { size })}
                      className={`w-full / text-center text-[13px] / bg-white
                      / outline outline-1 ${button.size === size ? "outline-[#4285F4] text-[#4285F4]" : "outline-[#D9D9D9] text-black"}
                      / p-[0.5rem] / rounded-[0.5rem]`}
                    >
                      {PORTFOLIO_BUTTON_SIZE_LABELS[size]}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
